import { DD } from "@/constants/testIds";
import { FileSearch, Globe2, Scale, ShieldAlert, ArrowLeft } from "lucide-react";

export default function EmptyState() {
    return (
        <div
            data-testid={DD.emptyState}
            className="flex-1 relative flex items-center overflow-hidden bg-ciff-paper"
        >
            {/* Subtle decorative magenta vertical bar — echoes CIFF logo treatment */}
            <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-ciff-magenta" />

            {/* Large outline circle, top-right, very soft */}
            <svg
                className="absolute -top-20 -right-20 opacity-[0.06] pointer-events-none"
                width="640" height="640" viewBox="0 0 100 100" aria-hidden="true"
            >
                <circle cx="50" cy="50" r="40" fill="none" stroke="hsl(var(--ciff-magenta))" strokeWidth="2" />
            </svg>

            <div className="relative max-w-3xl px-16 py-14 z-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 border border-ciff-rule bg-ciff-paper2">
                    <span className="w-1.5 h-1.5 rounded-full bg-ciff-magenta" />
                    <span className="text-[10px] uppercase tracking-[0.22em] text-ciff-ink font-mono">
                        Partnership Screening
                    </span>
                </div>

                <h1 className="font-heading text-5xl sm:text-6xl font-semibold tracking-tight text-ciff-ink leading-[1.04] mt-7">
                    Due diligence,<br />
                    for a world where<br />
                    <span className="ciff-highlight">every child can flourish</span>
                </h1>

                <p className="mt-8 text-ciff-inkSoft text-[17px] leading-relaxed max-w-2xl font-body">
                    Enter the legal name and registration identifier of any prospective
                    grantee. The platform runs anchored open-source searches across
                    regulator filings, sanctions lists, leadership profiles and news,
                    then composes a structured screening report with a colour-coded
                    risk dashboard — typically in under two minutes.
                </p>

                <div className="grid grid-cols-2 gap-4 mt-12 max-w-2xl">
                    <Card
                        icon={<Scale className="h-4 w-4" strokeWidth={1.5} />}
                        title="Registry-anchored"
                        body="Country-routed searches: IRS 990s, Charity Commission, FCRA, ACNC, CRA…"
                    />
                    <Card
                        icon={<ShieldAlert className="h-4 w-4" strokeWidth={1.5} />}
                        title="Sanctions screened"
                        body="OFAC, UN, EU, OFSI and World Bank debarment — surfaced, not asserted."
                    />
                    <Card
                        icon={<FileSearch className="h-4 w-4" strokeWidth={1.5} />}
                        title="15-section report"
                        body="3,000–4,500 words. RAG dashboard across 11 dimensions. Markdown + PDF."
                    />
                    <Card
                        icon={<Globe2 className="h-4 w-4" strokeWidth={1.5} />}
                        title="India FCRA module"
                        body="Conditional module triggers when Indian operations or affiliates surface."
                    />
                </div>

                <div className="mt-14 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-ciff-inkSoft font-mono">
                    <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
                    Start by entering an NGO name in the sidebar
                </div>
            </div>
        </div>
    );
}

function Card({ icon, title, body }) {
    return (
        <div className="bg-ciff-paper border border-ciff-rule px-5 py-4 transition-all duration-150 hover:border-ciff-magenta hover:-translate-y-px">
            <div className="flex items-center gap-2 text-ciff-magenta">
                {icon}
                <div className="font-heading text-[15px] font-semibold text-ciff-ink">{title}</div>
            </div>
            <p className="text-[12.5px] text-ciff-inkSoft mt-2 leading-snug font-body">{body}</p>
        </div>
    );
}
